/**
 * Error handling utilities
 * 
 * Provides common error checking and parsing functions to avoid duplication
 * across services and components.
 * 
 * @date 2026-02-19
 */

/**
 * Check if an error is an AbortError (typically from cancelled requests).
 * AbortErrors are expected during component unmounting, navigation, or request cancellation
 * and should generally be handled silently.
 * 
 * @param error - The error to check (can be Error, ServiceError, or unknown)
 * @returns true if the error is an AbortError
 * 
 * @example
 * ```typescript
 * try {
 *   await someAsyncOperation();
 * } catch (error) {
 *   if (isAbortError(error)) {
 *     return; // Silent handling
 *   }
 *   logger.error("Operation failed:", error);
 * }
 * ```
 */
export function isAbortError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === 'AbortError';
  }
  
  if (error && typeof error === 'object') {
    // Check for Supabase/typed error objects with message property
    if ('message' in error && typeof error.message === 'string') {
      return error.message.includes('AbortError');
    }
    // Check for name property (some error objects)
    if ('name' in error && error.name === 'AbortError') {
      return true;
    }
  }
  
  return false;
}

/**
 * Parse various error types into a user-friendly string message.
 * Handles Error instances, objects with message/error properties, and plain strings.
 * 
 * @param error - The error to parse
 * @param fallback - Default message if error cannot be parsed (default: 'An unknown error occurred')
 * @returns A user-friendly error message string
 * 
 * @example
 * ```typescript
 * const message = parseErrorMessage(error, 'Operation failed');
 * toast.error(message);
 * ```
 */
export function parseErrorMessage(error: unknown, fallback: string = 'An unknown error occurred'): string {
  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
    if (error instanceof Error) {
      return error.message;
    }
  }
  if (typeof error === 'string') {
    return error;
  }
  return fallback;
}
